import { IApplicationState } from '@Site/rootReducer'
import filter from 'lodash/filter'
import some from 'lodash/some'
import map from 'lodash/map'
import every from 'lodash/every'
import find from 'lodash/find'
import flatMap from 'lodash/flatMap'
import { createSelector } from 'reselect'
import getShouldAllowToAddLanguages from '@Utils/GetShouldAllowToAddLanguages'
import getShouldAllowToGenerateClips from '@Utils/GetShouldAllowToGenerateClips'
import getProjectVersionDisplayKind from '@Utils/GetProjectVersionDisplayKind'

export const PROCESSED_SUBTITLE_STAGES = [
  'new',
  'done',
  'waiting_customer_validation',
  'processed',
  'ready',
  'claim'
]

const PROCESSING_SUBTITLE_STAGES = [
  'processing',
  'generating_voice',
  'lip_syncing',
  'separating_audio',
  'generating_diarization'
]

import {
  IProject,
} from './types'

import {
  IProjectVersion,
} from '@SiteModules/ProjectVersion/types'

import {
  ILabel,
} from '@SiteModules/Label/types'

export const getProjects = (state: IApplicationState): IProject[] => state.projects.projects
export const getSelectedProjectIds = (state: IApplicationState): string[] => state.projects.selectedProjectIds
export const getCurrentPage = (state: IApplicationState): number => state.projects.page.currentPage
export const getTotalPages = (state: IApplicationState): number => state.projects.page.totalPages
export const getTotalCount = (state: IApplicationState): number => state.projects.page.totalCount
export const getShouldCheckAutoFillList = (state: IApplicationState): boolean => state.projects.page.shouldCheckAutoFillList
export const getShouldCheckAutoFillGrid = (state: IApplicationState): boolean => state.projects.page.shouldCheckAutoFillGrid
export const getListActive = (state: IApplicationState): boolean => state.projects.page.listActive
export const getHasLoadedInitially = (state: IApplicationState): boolean => state.projects.hasLoadedInitially
export const getLoading = (state: IApplicationState): boolean => state.projects.loading
export const getLabels = (state: IApplicationState): ILabel[] => state.labels.labels // Importing it from SiteModules/Label/selectors creates a circular dependency and therefore an error
export const getSearchText = (state: IApplicationState): string => state.projects.searchText
export const getStartingBulkExportation = (state: IApplicationState): boolean => state.projects.startingBulkExportation

export const getProjectVersions = createSelector(
  [getProjects],
  (projects) => flatMap(projects, p => p.projectVersions)
)

export const getOriginalProjectVersions = createSelector(
  [getProjectVersions],
  (projectVersions) => filter(projectVersions, pv => !pv.attributes.isRepurposed)
)

export const getSubtitles = createSelector(
  [getProjectVersions],
  (projectVersions) => flatMap(projectVersions, pv => pv.subtitles)
)

export const getProcessingOrExportingSubtitles = createSelector(
  [getSubtitles],
  (subtitles) => filter(subtitles,
    subtitle => PROCESSING_SUBTITLE_STAGES.includes(subtitle.attributes.stage) || subtitle.attributes.isExporting
  )
)

export const getSubtitleIdsToUpdate = createSelector(
  [getProcessingOrExportingSubtitles],
  (processingOrExportingSubtitles) => processingOrExportingSubtitles.map(subtitle => subtitle.id)
)

export const getDownloadingProjectVersions = createSelector(
  [getProjectVersions],
  (projectVersions) => filter(projectVersions, projectVersion =>
    projectVersion.attributes.status === 'downloading'
  )
)

export const getPendingClipProjectVersions = createSelector(
  [getProjectVersions],
  (projectVersions) => filter(projectVersions, projectVersion =>
    getProjectVersionDisplayKind(projectVersion.attributes) === 'pending_clip'
  )
)

export const getProjectVersionIdsToUpdate = createSelector(
  [getDownloadingProjectVersions, getPendingClipProjectVersions],
  (downloadingProjectVersions, pendingClipProjectVersions) => map(
    [...downloadingProjectVersions, ...pendingClipProjectVersions],
    projectVersion => projectVersion.id
  )
)

export const getShouldUpdateProjectVersionsAndSubtitles = createSelector(
  [getSubtitleIdsToUpdate, getProjectVersionIdsToUpdate],
  (subtitleIdsToUpdate, projectVersionIdsToUpdate) => !!subtitleIdsToUpdate.length || !!projectVersionIdsToUpdate.length
)

export const getSelectedProjects = createSelector(
  [getProjects, getSelectedProjectIds],
  (projects, selectedProjectIds) => filter(projects, project => selectedProjectIds.includes(project.id))
)

export const getSelectedProjectTitles = createSelector(
  [getSelectedProjects],
  (selectedProjects) => map(selectedProjects, selectedProject => selectedProject.attributes.title)
)

export const getOriginalProjectVersionsOfSelectedProjects = createSelector(
  [getSelectedProjects],
  (selectedProjects) => map(selectedProjects, selectedProject => find(selectedProject.projectVersions, pv => !pv.attributes.isRepurposed) as IProjectVersion)
)

export const getOriginalProjectVersionIdsOfSelectedProjects = createSelector(
  [getOriginalProjectVersionsOfSelectedProjects],
  (originalProjectVersionsOfSelectedProjects) => map(originalProjectVersionsOfSelectedProjects, pv => pv.id)
)

export const getAllSelectedProjectsAllowToAddLanguages = createSelector(
  [getOriginalProjectVersionsOfSelectedProjects],
  selectedProjectVersions => every(selectedProjectVersions, selectedProjectVersion => getShouldAllowToAddLanguages(selectedProjectVersion.attributes))
)

export const getAllSelectedProjectsAllowToGenerateClips = createSelector(
  [getOriginalProjectVersionsOfSelectedProjects],
  selectedProjectVersions => every(selectedProjectVersions, selectedProjectVersion => getShouldAllowToGenerateClips(selectedProjectVersion.attributes, selectedProjectVersion.subtitles))
)

export const getAllSelectedProjectsBelongToTtsProjects = createSelector(
  [getOriginalProjectVersionsOfSelectedProjects],
  selectedProjectVersions => every(selectedProjectVersions, selectedProjectVersion => selectedProjectVersion.attributes.belongsToTtsProject)
)

export const getAllSelectedProjectsCanManageSharing = createSelector(
  [getSelectedProjects],
  selectedProjects => every(selectedProjects, selectedProject => selectedProject.attributes.canManageProjectSharing)
)

export const getProjectsWithLanguageSelected = createSelector(
  [getOriginalProjectVersionsOfSelectedProjects],
  selectedProjectVersions => some(selectedProjectVersions, selectedProjectVersion => selectedProjectVersion.attributes.language)
)

export const getProjectsWithFreeCaptioningSelected = createSelector(
  [getOriginalProjectVersionsOfSelectedProjects],
  selectedProjectVersions => some(selectedProjectVersions, selectedProjectVersion => selectedProjectVersion.attributes.hasBeenCaptionedForFree)
)

export const getProjectsWithAnySubscriptionSubtitlesSelected = createSelector(
  [getOriginalProjectVersionsOfSelectedProjects],
  selectedProjectVersions => some(selectedProjectVersions, selectedProjectVersion => selectedProjectVersion.attributes.hasAnySubscriptionSubtitles)
)

export const getProjectsWithAtLeastOneProcessedSubtitleSelected = createSelector(
  [getOriginalProjectVersionsOfSelectedProjects],
  selectedProjectVersions => every(selectedProjectVersions, selectedProjectVersion => some(selectedProjectVersion.subtitles, subtitle => PROCESSED_SUBTITLE_STAGES.includes(subtitle.attributes.stage)))
)

export const getProjectsWithLanguages = createSelector(
  [getProjects],
  (projects) => filter(projects, project => {
    const originalProjectVersion = find(project.projectVersions, pv => !pv.attributes.isRepurposed) as IProjectVersion

    return originalProjectVersion.subtitles.length > 0 || originalProjectVersion.attributes.isPreparingRepurposedClips
  })
)

export const getProjectLabelCaptions = createSelector(
  [getProjects, getLabels],
  (projects, labels) => {
    return (
      map(projects, project => {
        return (
          {
            projectId: project.id,
            captions: map(filter(labels, label => label.attributes.projectIds.includes(project.id)), label => label.attributes.caption)
          }
        )
      })
    )
  }
)
